import { RulebookPage } from "@/components/RulebookPage";
import { Card, CardContent } from "@/components/ui/card";

export function ChallengeOf13() {
  return (
    <RulebookPage title="The Challenge of 13">
      <p className="text-sm text-muted-foreground mb-3">
        Every game of Ultimate Warlock revolves around a special festival, known
        as the Challenge of 13.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        This is an ancient tradition, conceived in times long past, when 13 of
        the most influential Patrons struck a dark bargain with one another.
        Together, they devised the Challenge of 13, a contest designed to
        exploit the folly of ambitious mortals.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Twice a year, during the solstices when the borders of reality are
        stretched thin, the Challenge of 13 begins.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        If the Warlocks who take up the Challenge are able to summon all 13
        Patrons within 48 hours, they win back a part of their soul. Previous
        Patrons who are no longer among the Thirteen may still be summoned, but
        do not count towards completing the Challenge.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Should they fall short by even one, a Dark Offering will be torn away
        from each of them without recompense, to be devoured by the hungry
        Patrons.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        The Challenge of 13 is won, or lost, as a group - victory belongs to
        all, or none.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Time and again, Warlocks have travelled from many worlds to gamble their
        essence in pursuit of forbidden strength, and take a step closer to
        becoming the Ultimate Warlock.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Sometimes they have won, sometimes they have lost, and still the game
        goes on.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Do not mistake this for a fair contest. The Patrons will actively resist
        your victory. They deceive. They interfere. They hunger.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">Festival Games</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Over the ages, the Challenge of 13 has grown from a select gathering of
        ambitious Warlocks, to a multiversal festival, where people of all kinds
        flock together to trade, share secrets, make connections, and compete in
        games.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        The Patrons themselves have deigned to sponsor some of these festival
        attractions, and encourage Warlocks to compete with one another by
        offering tantalising prizes to those who prove their skill and power.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        The timing for these special games can be found in the Festival Schedule
        over the page, and are explained in detail in the Festival Games section
        after.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">Festival Missions</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Patrons are always willing to reward Warlocks who go above and beyond to
        extend their influence in the mortal world.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        When a Warlock meets a Patron, they may request the opportunity to
        perform special favours in exchange for a free Otherworldly Gift. These
        special missions may range from playing simple games, to accomplishing
        mighty deeds, depending on the Patron&apos;s mood.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        However, indulging a Patron’s whims is rarely simple, and every act
        pulls the Warlock deeper into their designs, even if it appears on the
        surface the Warlock is receiving the better deal...
      </p>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Summoning a Patron</h2>
      <p className="text-sm text-muted-foreground mb-3">
        At the Challenge grounds, you will find three summoning circles that can
        be activated at specific times.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        However, not just any Warlock can summon forth any Patron. You must get
        their attention, and then perform a ritual to open the gateway.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        This is done by completing Patron Challenges, smaller tasks within the
        Challenge of 13 that spark the interest of these eldritch monsters.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        All of the Patrons and their Patron Challenges can be found in the rules
        for Character Creation. They can include tasks such as getting away with
        a lie, making other players laugh, fighting duels, and creating art.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Once a Warlock has completed all of the Patron Challenges for a specific
        Patron, that Patron is now focussed (for better or worse) on that person
        - and that Warlock can attempt a summoning ritual!
      </p>
      <h2 className="text-xl font-semibold mt-6 mb-2">The Summoning Ritual</h2>
      <ol className="list-decimal list-inside text-sm text-muted-foreground mb-3 space-y-1">
        <li>
          The summoning ritual must be performed at the scheduled time, within a
          circle of 13 stones inscribed with magic runes.
        </li>
        <li>
          A maximum of 8 Warlocks may be present at a single summoning. Any more
          than that, and the ritual automatically fails.
        </li>
        <li>
          The Warlock performing the summoning must have completed the Patron’s
          Challenges that day.
        </li>
        <li>
          The summoner must roleplay performing a ritual at least one minute
          long, paying one gold coin for materials.
        </li>
        <li>
          As part of this ritual the summoner must call out the Patron’s name,
          and describe to the Patron exactly how they completed its Challenges
          (many pretentious Warlocks take this as a chance to boast about their
          accomplishments in front of other Warlocks, but I’m sure you’re above
          that behaviour).
        </li>
        <li>
          When the ritual is complete, if all the requirements are met and the
          Patron is satisfied, the Patron will be temporarily summoned to the
          area within the ritual stones.
        </li>
        <li>
          The summoner, and any other warlocks that accompany them, must stay
          within the circle for the duration of the ritual. Once the Patron is
          summoned, the only way to enter or leave is with their permission…
        </li>
        <li>
          Each individual Warlock may not summon the same Patron twice during
          the Challenge of 13.
        </li>
        <li>
          Each Warlock who is the first to summon each Patron during the
          Challenge of 13 is eligible to receive a free Otherworldly Gift.
        </li>
      </ol>
      <p className="text-sm text-muted-foreground mb-3">
        Any Warlock present at a successful summoning may make any Dark
        Offerings to that Patron in exchange for any Otherworldly Gifts.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        They may also ask if the Patron is willing to give away any Otherworldly
        Gifts for free - though this always comes with a special task or
        challenge for the Warlock to complete.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Be careful to show Patrons the proper respect! Though they may have
        stooped to take on a physical form while visiting this world, they are
        immortal, invulnerable beings with the power of gods, and many arrogant
        Warlocks have met fates worse than death for the tiniest of perceived
        insults.
      </p>
      <Card className="mb-3">
        <CardContent className="text-sm text-muted-foreground">
          A Warlock is allowed to attempt a summoning if they have completed a
          Patron&apos;s Challenges, even if they have not previously pledged
          themselves to that Patron, or have any of their Otherworldly Gifts.
        </CardContent>
      </Card>
    </RulebookPage>
  );
}
